import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, RefreshCw } from "lucide-react";
import clsx from "clsx";
import {
  ActionItems as ActionItemsApi,
  Activities,
  Config,
  DealContacts,
  Handoffs,
  Integrations,
} from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import type { ActionItem, Activity, ActivityDetail, Deal, Handoff, SyncSummary } from "@/api/types";
import { ACTIVITY_ROLES, roleVar } from "@/lib/colors";
import { fmtDayShort } from "@/lib/format";
import { toast } from "@/lib/feedback";
import { todayISO } from "@/lib/people";
import { useAuthStore } from "@/store/auth";
import { Avatar } from "@/components/ui";
import { sideOf, type Side } from "@/lib/sides";
import { Bubble, DayDivider, SharedEvent, Silence, StageChange } from "./Chat";
import Journey from "./Journey";

/** Gaps longer than this between interactions are called out in the chat. */
const SILENCE_DAYS = 10;

type Composable = "call" | "email" | "meeting" | "note" | "hospitality";
const COMPOSE: { value: Composable; label: string }[] = [
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "meeting", label: "Meeting" },
  { value: "note", label: "Note" },
  { value: "hospitality", label: "Hospitality" },
];

/** `at` is the exact time it happened (null when only the day is known);
 *  `logged` is when it was entered, used only to order untimed entries. */
type Event =
  | { kind: "activity"; day: string; at: string | null; logged: string; activity: Activity }
  | { kind: "handoff"; day: string; at: string | null; logged: string; handoff: Handoff };

const ms = (iso: string) => new Date(iso).getTime();

/** Oldest first. Within a day: untimed entries first (in the order they were
 *  logged), then timed ones by the moment they happened. Compares instants,
 *  not strings, so mixed timezone offsets still sort correctly. */
function byStoryOrder(x: Event, y: Event): number {
  if (x.day !== y.day) return x.day < y.day ? -1 : 1;
  if (x.at && y.at) return ms(x.at) - ms(y.at) || ms(x.logged) - ms(y.logged);
  if (x.at) return 1;
  if (y.at) return -1;
  return ms(x.logged) - ms(y.logged);
}

function syncLine(s: SyncSummary): string {
  if (s.contacts_matched === 0)
    return "No contacts with an email address on this deal to match against.";
  const n = (v: number, w: string) => `${v} ${w}${v === 1 ? "" : "s"}`;
  let line = `Added ${n(s.emails_added, "email")} and ${n(s.meetings_added, "meeting")}`;
  if (s.skipped_duplicates > 0) line += ` (${s.skipped_duplicates} already here)`;
  return line + ".";
}

/** The deal's story: interactions, handoffs and stage changes, oldest at the
 *  top and newest at the bottom, with the composer underneath — like a conversation. */
export default function Story({ deal }: { deal: Deal }) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const canEdit = deal.can_edit;
  const today = todayISO();
  const endRef = useRef<HTMLDivElement>(null);

  const activitiesQuery = useQuery({
    queryKey: ["activities", deal.id],
    queryFn: () => Activities.list(deal.id),
  });
  const handoffsQuery = useQuery({
    queryKey: ["deal-handoffs", deal.id],
    queryFn: () => Handoffs.forDeal(deal.id),
  });
  const itemsQuery = useQuery({
    queryKey: ["action-items", deal.id],
    queryFn: () => ActionItemsApi.list(deal.id),
  });
  const contactsQuery = useQuery({
    queryKey: ["deal-contacts", deal.id],
    queryFn: () => DealContacts.list(deal.id),
  });
  const integrationsQuery = useQuery({ queryKey: ["integrations"], queryFn: Integrations.list });
  const teamQuery = useQuery({ queryKey: ["owners"], queryFn: () => Config.listOwners() });
  const googleConnected =
    integrationsQuery.data?.some((p) => p.provider === "google" && p.status === "connected") ??
    false;

  const byActivity = useMemo(() => {
    const map: Record<string, ActionItem[]> = {};
    for (const i of itemsQuery.data ?? []) {
      if (i.parent_activity_id) (map[i.parent_activity_id] ??= []).push(i);
    }
    return map;
  }, [itemsQuery.data]);

  const events = useMemo(() => {
    const list: Event[] = [
      ...(activitiesQuery.data ?? []).map<Event>((a) => ({
        kind: "activity",
        day: a.date,
        at: a.occurred_at,
        logged: a.created_at,
        activity: a,
      })),
      ...(handoffsQuery.data ?? []).map<Event>((h) => ({
        kind: "handoff",
        day: h.created_at.slice(0, 10),
        at: h.created_at,
        logged: h.created_at,
        handoff: h,
      })),
    ];
    return list.sort(byStoryOrder);
  }, [activitiesQuery.data, handoffsQuery.data]);

  // Who wrote what: teammates (by name or email) are "us", everyone else "them".
  const meName = me?.name;
  const meEmail = me?.email;
  const sides = useMemo(() => {
    const team = new Set<string>();
    for (const u of teamQuery.data ?? []) team.add(u.name.trim().toLowerCase());
    if (meName) team.add(meName.trim().toLowerCase());
    if (meEmail) team.add(meEmail.trim().toLowerCase());
    const map = new Map<string, Side>();
    for (const a of activitiesQuery.data ?? []) map.set(a.id, sideOf(a, team));
    return map;
  }, [teamQuery.data, activitiesQuery.data, meName, meEmail]);

  // Land on the newest entry when the story first loads.
  const loaded = activitiesQuery.isSuccess && handoffsQuery.isSuccess;
  useEffect(() => {
    if (loaded) endRef.current?.scrollIntoView({ block: "end" });
  }, [loaded, deal.id]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["activities", deal.id] });
    void qc.invalidateQueries({ queryKey: ["deal", deal.id] });
    void qc.invalidateQueries({ queryKey: ["deal-people", deal.id] });
    void qc.invalidateQueries({ queryKey: ["deals"] });
  };
  const refreshItems = () => {
    void qc.invalidateQueries({ queryKey: ["action-items", deal.id] });
    void qc.invalidateQueries({ queryKey: ["my-work"] });
    void qc.invalidateQueries({ queryKey: ["deal-access", deal.id] });
  };

  const syncMutation = useMutation({
    mutationFn: () => Activities.syncGoogle(deal.id),
    onSuccess: (s) => {
      refresh();
      if (s.errors.length) toast.err(`${syncLine(s)}\n${s.errors.join("; ")}`);
      else toast.ok(syncLine(s));
    },
    onError: (e) => toast.err(`Sync failed: ${apiErrorMessage(e)}`),
  });

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="flex items-center justify-between px-4 md:px-6 pt-4 pb-2">
        <h3 className="section-title">Story</h3>
        {!integrationsQuery.isLoading && !googleConnected ? (
          <Link to="/settings" className="text-sm text-accent-azure hover:underline">
            Connect Google to sync email
          </Link>
        ) : (
          <button
            className="btn btn-ghost !py-1 text-sm"
            onClick={() => syncMutation.mutate()}
            disabled={!googleConnected || syncMutation.isPending}
            title="Pull the last 90 days of Gmail and Calendar for this deal's contacts"
          >
            <RefreshCw className={clsx("w-3.5 h-3.5", syncMutation.isPending && "animate-spin")} />
            {syncMutation.isPending ? "Syncing…" : "Sync from Gmail"}
          </button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pb-4">
        {loaded && (
          <Journey
            deal={deal}
            activities={activitiesQuery.data ?? []}
            handoffs={handoffsQuery.data ?? []}
            sides={sides}
            today={today}
          />
        )}
        <div className="px-4 md:px-6">
          {!loaded ? (
            <p className="text-md text-mut-500 py-6">Loading…</p>
          ) : events.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-md font-medium text-mut-200">Nothing here yet</p>
              <p className="text-sm text-mut-500 mt-1">
                Log the first call or note below, or sync from Gmail.
              </p>
            </div>
          ) : (
            <div className="space-y-4 pt-1">
              {events.map((ev, i) => {
                const prev = events[i - 1];
                const gap = prev
                  ? Math.round(
                      (new Date(ev.day).getTime() - new Date(prev.day).getTime()) / 86_400_000,
                    )
                  : 0;
                const head = (
                  <>
                    {gap >= SILENCE_DAYS && <Silence days={gap} />}
                    {(!prev || prev.day !== ev.day) && <DayDivider day={ev.day} />}
                  </>
                );
                if (ev.kind === "handoff") {
                  return (
                    <div key={ev.handoff.id} className="space-y-4">
                      {head}
                      <div className="mx-auto max-w-[560px]">
                        <HandoffCard
                          handoff={ev.handoff}
                          meId={me?.id}
                          isAdmin={me?.role === "admin"}
                        />
                      </div>
                    </div>
                  );
                }
                const a = ev.activity;
                const side = sides.get(a.id) ?? "us";
                return (
                  <div key={a.id} className="space-y-4">
                    {head}
                    {a.type === "status_change" ? (
                      <StageChange activity={a} />
                    ) : side === "both" ? (
                      <SharedEvent activity={a} today={today} />
                    ) : (
                      <Bubble
                        activity={a}
                        side={side}
                        items={byActivity[a.id] ?? []}
                        canEdit={canEdit}
                        currentUserId={me?.id ?? ""}
                        onItemsMutated={refreshItems}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div ref={endRef} />
      </div>

      {canEdit && (
        <Composer
          dealId={deal.id}
          contacts={(contactsQuery.data ?? []).map((l) => l.contact)}
          onLogged={refresh}
        />
      )}
    </div>
  );
}

// ─── Handoff card ───────────────────────────────────────────────

function HandoffCard({
  handoff: h,
  meId,
  isAdmin,
}: {
  handoff: Handoff;
  meId?: string;
  isAdmin: boolean;
}) {
  const qc = useQueryClient();
  const settle = (verb: string) => () => {
    void qc.invalidateQueries({ queryKey: ["deal-handoffs", h.deal_id] });
    void qc.invalidateQueries({ queryKey: ["deal", h.deal_id] });
    void qc.invalidateQueries({ queryKey: ["deal-access", h.deal_id] });
    void qc.invalidateQueries({ queryKey: ["deal-people", h.deal_id] });
    void qc.invalidateQueries({ queryKey: ["deals"] });
    void qc.invalidateQueries({ queryKey: ["handoffs"] });
    void qc.invalidateQueries({ queryKey: ["my-work"] });
    toast.ok(verb);
  };
  const fail = (e: unknown) => toast.err(apiErrorMessage(e));
  const accept = useMutation({
    mutationFn: () => Handoffs.accept(h.id),
    onSuccess: settle("You own this deal now."),
    onError: fail,
  });
  const decline = useMutation({
    mutationFn: () => Handoffs.decline(h.id),
    onSuccess: settle("Handoff declined."),
    onError: fail,
  });
  const cancel = useMutation({
    mutationFn: () => Handoffs.cancel(h.id),
    onSuccess: settle("Handoff cancelled."),
    onError: fail,
  });
  const busy = accept.isPending || decline.isPending || cancel.isPending;
  const lines = (h.note ?? "")
    .split("\n")
    .map((l) => l.replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean);

  const stateLine: Record<Handoff["status"], string> = {
    pending: `Waiting for ${h.to_name ?? "them"} to accept`,
    accepted: `Accepted by ${h.to_name ?? "them"}${h.responded_at ? ` · ${fmtDayShort(h.responded_at)}` : ""}`,
    declined: `Declined by ${h.to_name ?? "them"}`,
    cancelled: "Cancelled",
  };

  return (
    <>
      <span
        className="absolute left-0 -translate-x-1/2 top-3 grid h-6 w-6 place-items-center rounded-full ring-4 ring-ink-950"
        style={{
          ...roleVar(ACTIVITY_ROLES.handoff),
          background: "rgb(var(--c) / 0.14)",
          color: "rgb(var(--c))",
        }}
      >
        <ArrowRight className="w-3 h-3" />
      </span>
      <div className="rounded-2xl p-4 space-y-2.5" style={{ background: "var(--grad-handoff)" }}>
        <div className="flex items-center gap-2 text-sm flex-wrap">
          <Avatar name={h.from_name} size={20} />
          <b className="font-semibold">{h.from_name ?? "Someone"}</b>
          <ArrowRight className="w-3.5 h-3.5 text-accent-violet" />
          <Avatar name={h.to_name} size={20} />
          <b className="font-semibold">{h.to_name}</b>
          <span className="text-mut-500">handed this over · {h.stage}</span>
        </div>
        {lines.length > 0 && (
          <ul className="list-disc pl-5 text-md space-y-0.5">
            {lines.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={clsx(
              "text-sm font-medium",
              h.status === "accepted" && "text-accent-green",
              h.status === "pending" && "text-accent-violet",
              (h.status === "declined" || h.status === "cancelled") && "text-mut-500",
            )}
          >
            {stateLine[h.status]}
          </span>
          {h.status === "pending" && h.to_user_id === meId && (
            <span className="ml-auto flex gap-2">
              <button className="btn !py-1" disabled={busy} onClick={() => decline.mutate()}>
                Decline
              </button>
              <button
                className="btn btn-primary !py-1"
                disabled={busy}
                onClick={() => accept.mutate()}
              >
                Accept
              </button>
            </span>
          )}
          {h.status === "pending" &&
            h.to_user_id !== meId &&
            (h.from_user_id === meId || isAdmin) && (
              <button
                className="btn btn-ghost !py-1 ml-auto text-sm"
                disabled={busy}
                onClick={() => cancel.mutate()}
              >
                Cancel handoff
              </button>
            )}
        </div>
      </div>
    </>
  );
}

// ─── Composer ───────────────────────────────────────────────────

function Composer({
  dealId,
  contacts,
  onLogged,
}: {
  dealId: string;
  contacts: { id: string; name: string; email: string | null }[];
  onLogged: () => void;
}) {
  const [type, setType] = useState<Composable>("call");
  const [text, setText] = useState("");
  const [date, setDate] = useState(todayISO);
  const [time, setTime] = useState(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  });
  const [cost, setCost] = useState("");
  const [withIds, setWithIds] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: () => {
      const participants = contacts
        .filter((c) => withIds.includes(c.id))
        .map((c) => ({ name: c.name, email: c.email ?? "" }));
      const detail: Partial<ActivityDetail> = {};
      if (participants.length) detail.participants = participants;
      if (type === "hospitality" && cost) detail.cost_amount = Number(cost);
      return Activities.create(dealId, {
        type,
        date,
        text: text.trim(),
        detail: Object.keys(detail).length ? detail : undefined,
        // Local date + time → an exact instant, so the story orders it correctly.
        occurred_at: time ? new Date(`${date}T${time}`).toISOString() : undefined,
      });
    },
    onSuccess: () => {
      setText("");
      setCost("");
      setWithIds([]);
      onLogged();
      toast.ok("Logged.");
    },
    onError: (e) => toast.err(`Couldn't log it: ${apiErrorMessage(e)}`),
  });

  const submit = () => {
    if (text.trim() && !mutation.isPending) mutation.mutate();
  };

  return (
    <div className="border-t border-fg/[0.07] bg-ink-950/90 backdrop-blur px-3 md:px-6 py-3 space-y-2.5">
      <div
        className="flex flex-wrap items-center gap-1.5"
        role="radiogroup"
        aria-label="What happened"
      >
        {COMPOSE.map((c) => (
          <button
            key={c.value}
            role="radio"
            aria-checked={type === c.value}
            onClick={() => setType(c.value)}
            className={clsx(
              "px-2.5 py-1 rounded-full text-sm font-medium transition-colors",
              type === c.value ? "text-white" : "hover:brightness-95",
            )}
            style={{
              ...roleVar(ACTIVITY_ROLES[c.value]),
              background: type === c.value ? "rgb(var(--c))" : "rgb(var(--c) / 0.1)",
              color: type === c.value ? "#fff" : "rgb(var(--c))",
            }}
          >
            {c.label}
          </button>
        ))}
        <input
          type="date"
          className="input !w-auto !py-1 text-sm ml-auto"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          aria-label="Date"
        />
        <input
          type="time"
          className="input !w-auto !py-1 text-sm"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          aria-label="Time"
        />
        {type === "hospitality" && (
          <input
            type="number"
            min="0"
            step="0.01"
            className="input !w-24 !py-1 text-sm"
            placeholder="Cost €"
            aria-label="Cost in euros"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
          />
        )}
      </div>

      {contacts.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm text-mut-500 mr-1">With</span>
          {contacts.map((c) => {
            const on = withIds.includes(c.id);
            return (
              <button
                key={c.id}
                aria-pressed={on}
                onClick={() =>
                  setWithIds((ids) => (on ? ids.filter((x) => x !== c.id) : [...ids, c.id]))
                }
                className={clsx(
                  "inline-flex items-center gap-1.5 pl-1 pr-2.5 py-0.5 rounded-full text-sm border transition-colors",
                  on
                    ? "border-accent-azure/50 bg-accent-azure/10 text-mut-100"
                    : "border-fg/[0.1] text-mut-300 hover:border-fg/[0.2]",
                )}
              >
                <Avatar name={c.name} size={18} muted={!on} />
                {c.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex gap-2">
        <textarea
          className="input min-h-[44px] max-h-40 resize-y"
          rows={1}
          placeholder="Write what happened…"
          aria-label="What happened"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
          }}
        />
        <button
          className="btn btn-primary self-end"
          disabled={!text.trim() || mutation.isPending}
          onClick={submit}
        >
          Log
        </button>
      </div>
    </div>
  );
}
